// Cloudflare Workers / ブラウザ互換のカスタムロガー（vibelogger風）

export interface LogContext {
  type?: string
  category?: string
  [key: string]: any
}

export interface LogEntry {
  level: string
  message: string
  data?: any
  context?: LogContext
  timestamp: string
}

export class VibeLogger {
  private appName: string
  private environment: string
  private enableConsole: boolean
  private logs: LogEntry[] = []
  
  constructor(config: {
    appName: string
    environment?: string
    enableConsole?: boolean
  }) {
    this.appName = config.appName
    this.environment = config.environment || 'development'
    this.enableConsole = config.enableConsole ?? true
  }
  
  private formatLog(entry: LogEntry): string {
    const prefix = `[${this.appName}]`
    const emoji = this.getEmoji(entry.level)
    const message = `${prefix} ${emoji} ${entry.message}`
    
    if (entry.data) {
      return `${message}\n${JSON.stringify(entry.data, null, 2)}`
    }
    return message
  }
  
  private getEmoji(level: string): string {
    switch (level) {
      case 'step': return '🎯'
      case 'process': return '⚡'
      case 'info': return 'ℹ️'
      case 'debug': return '🔍'
      case 'warn': return '⚠️'
      case 'error': return '❌'
      case 'todo': return '📝'
      default: return '•'
    }
  }
  
  private log(level: string, message: string, data?: any) {
    const entry: LogEntry = {
      level,
      message,
      data,
      timestamp: new Date().toISOString()
    }
    
    this.logs.push(entry)
    
    if (this.enableConsole) {
      const formatted = this.formatLog(entry)
      
      switch (level) {
        case 'error':
          console.error(formatted)
          break
        case 'warn':
          console.warn(formatted)
          break
        case 'debug':
          if (this.environment === 'development') {
            console.debug(formatted)
          }
          break
        default:
          console.log(formatted)
      }
    }
  }
  
  step(message: string, data?: any) {
    this.log('step', message, data)
  }
  
  process(message: string, data?: any) {
    this.log('process', message, data)
  }
  
  info(message: string, data?: any) {
    this.log('info', message, data)
  }
  
  debug(message: string, data?: any) {
    this.log('debug', message, data)
  }
  
  warn(message: string, data?: any) {
    this.log('warn', message, data)
  }
  
  error(message: string, data?: any) {
    this.log('error', message, data)
  }
  
  todo(task: string, data?: any) {
    this.log('todo', `TODO: ${task}`, data)
  }
  
  getLogs(): LogEntry[] {
    return this.logs
  }
  
  clearLogs() {
    this.logs = []
  }
}